import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import Iframe from "react-iframe";
import { Redirect, Link } from "react-router-dom";
import {
  FaChartBar,
  FaCheck,
  FaFileAlt,
  FaSyncAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import MdlSelectSession from "../dialog/MdlSelectSession";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import uploadIcon from "../resources/images/upload-icon.png";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const ALL_CATEGORY = "ALL";

const REPORT_TYPES = [
  { value: "today", label: "Today", icon: "fa-calendar-day", color: "#10b981" },
  { value: "asOnDate", label: "As On Date", icon: "fa-history", color: "#06b6d4" },
  { value: "dateRange", label: "Date Range", icon: "fa-calendar-alt", color: "#3b82f6" },
  { value: "month", label: "Month Wise", icon: "fa-calendar", color: "#8b5cf6" },
];

const DATE_PRESETS = [
  { value: "today", label: "Today", icon: "fa-calendar-day", color: "#10b981" },
  { value: "yesterday", label: "Yesterday", icon: "fa-calendar-minus", color: "#6366f1" },
  { value: "thisWeek", label: "This Week", icon: "fa-calendar-week", color: "#3b82f6" },
  { value: "lastWeek", label: "Last Week", icon: "fa-calendar-week", color: "#a855f7" },
  { value: "last7Days", label: "Last 7 Days", icon: "fa-calendar", color: "#8b5cf6" },
  { value: "thisMonth", label: "This Month", icon: "fa-calendar-alt", color: "#f59e0b" },
  { value: "lastMonth", label: "Last Month", icon: "fa-calendar-check", color: "#ef4444" },
  { value: "last30Days", label: "Last 30 Days", icon: "fa-calendar", color: "#06b6d4" },
];

const FALLBACK_PRINTABLE = {
  sample_id: "",
  sample_name: "Expense Report",
  file_directory: `${process.env.REACT_APP_TARGET_DIR || ""}Printables/Report/`,
  file_name: "expense_report.php",
};

//Created By Cursor
function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

//Created By Cursor
function parseLooseDate(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    const d = new Date(value.slice(0, 10) + "T00:00:00");
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const m = value.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (m) {
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

//Created By Cursor
function buildSessionMonths(startRaw, endRaw) {
  const start = parseLooseDate(startRaw);
  const end = parseLooseDate(endRaw);
  if (!start || !end) return [];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

//Created By Cursor
function monthBounds(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return { from: "", to: "" };
  const year = Number(m[1]);
  const month = Number(m[2]);
  const lastDay = new Date(year, month, 0).getDate();
  return {
    from: `${m[1]}-${m[2]}-01`,
    to: `${m[1]}-${m[2]}-${String(lastDay).padStart(2, "0")}`,
  };
}

//Created By Cursor
function resolveSession() {
  const session = SessionManager.getCurrentSession();
  if (Array.isArray(session) && session[0]) {
    return {
      id: String(session[0].session_id || ""),
      name: String(session[0].session_name || ""),
      status: String(session[0].status || ""),
      startDate: String(session[0].start_date || ""),
      endDate: String(session[0].end_date || ""),
    };
  }
  if (session && typeof session === "object") {
    return {
      id: String(session.session_id || ""),
      name: String(session.session_name || ""),
      status: String(session.status || ""),
      startDate: String(session.start_date || ""),
      endDate: String(session.end_date || ""),
    };
  }
  return { id: "", name: "", status: "", startDate: "", endDate: "" };
}

//Created By Cursor
const ExpenseReport = () => {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(ALL_CATEGORY);
  const [reportType, setReportType] = useState("today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [fileUrl, setFileUrl] = useState("");
  const [height, setHeight] = useState("420px");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [sessionStartDate, setSessionStartDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState("");
  const iframeRef = useRef(null);
  const bootstrappedRef = useRef(false);
  const skipAutoPreviewRef = useRef(false);

  const monthOptions = useMemo(
    () => buildSessionMonths(sessionStartDate, sessionEndDate),
    [sessionStartDate, sessionEndDate]
  );

  const selectedCategory = useMemo(() => {
    if (selectedCategoryId === ALL_CATEGORY) {
      return { category_id: ALL_CATEGORY, category_name: "All categories" };
    }
    return (
      categories.find(
        (c) => String(c.category_id) === String(selectedCategoryId)
      ) || { category_id: ALL_CATEGORY, category_name: "All categories" }
    );
  }, [categories, selectedCategoryId]);

  //Created By Cursor
  const resolveDateBounds = useCallback(
    (type, from, to, monthYm) => {
      if (type === "today") {
        const today = formatDateLocal(new Date());
        return { from: today, to: today };
      }
      if (type === "asOnDate") {
        const today = formatDateLocal(new Date());
        const start = parseLooseDate(sessionStartDate);
        const fromDate = start ? formatDateLocal(start) : today;
        return { from: fromDate, to: today };
      }
      if (type === "month") {
        if (!monthYm) return { from: "", to: "" };
        return monthBounds(monthYm);
      }
      return { from: from || "", to: to || "" };
    },
    [sessionStartDate]
  );

  //Created By Cursor
  const buildFileUrl = useCallback(
    (sid, cat, from, to, rType, monthYm) => {
      if (!sid) return "";
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return "";

      const bounds = resolveDateBounds(rType, from, to, monthYm);
      if (rType === "dateRange" && (!bounds.from || !bounds.to)) return "";
      if (rType === "asOnDate" && (!bounds.from || !bounds.to)) return "";
      if (rType === "month" && (!bounds.from || !bounds.to)) return "";

      let directory = String(FALLBACK_PRINTABLE.file_directory || "").trim();
      const fileName = FALLBACK_PRINTABLE.file_name;
      if (!directory) directory = "Printables/Report/";
      if (directory && !directory.endsWith("/")) directory += "/";

      const params = new URLSearchParams();
      params.set("session_id", sid);
      params.set("school_code", schoolCode);
      params.set("entry_type", "ALL");
      params.set("category_id", cat?.category_id || ALL_CATEGORY);
      params.set("category_name", cat?.category_name || "All categories");
      params.set("report_type", rType || "today");
      if (bounds.from) params.set("date_from", bounds.from);
      if (bounds.to) params.set("date_to", bounds.to);
      if (rType === "month" && monthYm) params.set("year_month", monthYm);
      if (process.env.REACT_APP_SYSTEM_CODE) {
        params.set("system_code", process.env.REACT_APP_SYSTEM_CODE);
      }

      return `${process.env.REACT_APP_SERVER_NAME}${directory}${fileName}?${params.toString()}`;
    },
    [resolveDateBounds]
  );

  //Created By Cursor
  const refreshPreview = useCallback(
    (sid, cat, from, to, rType, monthYm) => {
      const url = buildFileUrl(sid, cat, from, to, rType, monthYm);
      console.log("[ExpenseReport] FULL URL>>>", url || "(empty)");
      if (!url) {
        setFileUrl("");
        setIframeLoading(false);
        return;
      }
      setIframeLoading(true);
      setHeight("420px");
      setFileUrl(url);
    },
    [buildFileUrl]
  );

  //Created By Cursor
  const applyPreview = useCallback(
    (overrides = {}) => {
      const sid = overrides.sid ?? sessionId;
      const cat = overrides.cat ?? selectedCategory;
      const from = overrides.from ?? dateFrom;
      const to = overrides.to ?? dateTo;
      const rType = overrides.rType ?? reportType;
      const monthYm = overrides.monthYm ?? selectedMonth;
      refreshPreview(sid, cat, from, to, rType, monthYm);
    },
    [
      sessionId,
      selectedCategory,
      dateFrom,
      dateTo,
      reportType,
      selectedMonth,
      refreshPreview,
    ]
  );

  //Created By Cursor
  const handleDateRangePreset = useCallback((preset) => {
    const today = new Date();
    let newStartDate = "";
    let newEndDate = "";

    switch (preset) {
      case "today":
        newStartDate = newEndDate = formatDateLocal(today);
        break;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        newStartDate = newEndDate = formatDateLocal(yesterday);
        break;
      }
      case "thisWeek": {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        newStartDate = formatDateLocal(startOfWeek);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "lastWeek": {
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        newStartDate = formatDateLocal(lastWeekStart);
        newEndDate = formatDateLocal(lastWeekEnd);
        break;
      }
      case "last7Days": {
        const last7Days = new Date(today);
        last7Days.setDate(today.getDate() - 7);
        newStartDate = formatDateLocal(last7Days);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "thisMonth": {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        newStartDate = formatDateLocal(startOfMonth);
        newEndDate = formatDateLocal(today);
        break;
      }
      case "lastMonth": {
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        newStartDate = formatDateLocal(firstDayLastMonth);
        newEndDate = formatDateLocal(lastDayLastMonth);
        break;
      }
      case "last30Days": {
        const last30Days = new Date(today);
        last30Days.setDate(today.getDate() - 30);
        newStartDate = formatDateLocal(last30Days);
        newEndDate = formatDateLocal(today);
        break;
      }
      default:
        return;
    }

    setDateRangePreset(preset);
    setDateFrom(newStartDate);
    setDateTo(newEndDate);
  }, []);

  //Created By Cursor
  const handleCalendarDateClick = (date) => {
    const dateStr = formatDateLocal(date);
    if (selectingStartDate || !dateFrom || dateStr < dateFrom) {
      setDateFrom(dateStr);
      setDateTo("");
      setSelectingStartDate(false);
      setDateRangePreset("custom");
    } else {
      setDateTo(dateStr);
      setSelectingStartDate(true);
      setDateRangePreset("custom");
    }
  };

  //Created By Cursor
  const navigateCalendarMonth = (direction) => {
    const next = new Date(calendarMonth);
    next.setMonth(next.getMonth() + direction);
    setCalendarMonth(next);
  };

  const generateCalendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let day = 1; day <= lastDay; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  }, [calendarMonth]);

  //Created By Cursor
  const isDateInRange = (date) => {
    if (!dateFrom || !dateTo) return false;
    const dateStr = formatDateLocal(date);
    return dateStr >= dateFrom && dateStr <= dateTo;
  };

  //Created By Cursor
  const isDateSelected = (date) => {
    const dateStr = formatDateLocal(date);
    return dateStr === dateFrom || dateStr === dateTo;
  };

  //Created By Cursor
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  //Created By Cursor
  const loadCategories = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setCategories([]);
      return [];
    }
    try {
      const res = await axios.get(`${API_BASE()}getDaybookAddExpenseData.php`, {
        params: { school_code: schoolCode },
      });
      const payload = res?.data;
      const nextCats =
        payload?.api_status === "OK" &&
        Array.isArray(payload?.data?.DaybookCategory)
          ? payload.data.DaybookCategory
          : [];
      setCategories(nextCats);
      return nextCats;
    } catch (error) {
      console.error("[ExpenseReport] categories error", error);
      setCategories([]);
      return [];
    }
  }, []);

  //Created By Cursor
  const loadPageData = useCallback(
    async ({ soft = false, sid } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      const activeSessionId = sid || sessionId || resolveSession().id;

      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!activeSessionId) {
        myToast.notify_warning("Session is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const nextCats = await loadCategories();
        let cat = {
          category_id: ALL_CATEGORY,
          category_name: "All categories",
        };
        if (selectedCategoryId !== ALL_CATEGORY) {
          const found = nextCats.find(
            (c) => String(c.category_id) === String(selectedCategoryId)
          );
          if (found) {
            cat = {
              category_id: found.category_id,
              category_name: found.category_name || "Category",
            };
          } else {
            setSelectedCategoryId(ALL_CATEGORY);
          }
        }

        if (reportType === "month" && !selectedMonth) {
          setFileUrl("");
          setIframeLoading(false);
        } else {
          refreshPreview(
            activeSessionId,
            cat,
            dateFrom,
            dateTo,
            reportType,
            selectedMonth
          );
        }
      } catch (error) {
        console.error("[ExpenseReport] load error", error);
        myToast.notify_warning("Failed to load expense report data.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      dateFrom,
      dateTo,
      loadCategories,
      refreshPreview,
      reportType,
      selectedCategoryId,
      selectedMonth,
      sessionId,
    ]
  );

  useEffect(() => {
    const session = resolveSession();
    setSessionId(session.id);
    setSessionName(session.name);
    setSessionStatus(session.status);
    setSessionStartDate(session.startDate);
    setSessionEndDate(session.endDate);

    const today = formatDateLocal(new Date());
    setDateFrom(today);
    setDateTo(today);

    const handleMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      loadPageData({ sid: sessionId });
      return;
    }
    loadPageData({ soft: true, sid: sessionId });
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  //Created By Cursor — default date-range preset when switching to Date Range
  useEffect(() => {
    if (reportType === "dateRange" && !dateRangePreset) {
      handleDateRangePreset("today");
    }
  }, [reportType, dateRangePreset, handleDateRangePreset]);

  //Created By Cursor — auto preview for today / date range
  useEffect(() => {
    if (!sessionId || loading) return;
    if (skipAutoPreviewRef.current) {
      skipAutoPreviewRef.current = false;
      return;
    }
    if (reportType === "today") {
      applyPreview({ rType: "today" });
      return;
    }
    if (reportType === "asOnDate") {
      const bounds = resolveDateBounds("asOnDate", "", "", "");
      if (bounds.from && bounds.to) {
        applyPreview({
          rType: "asOnDate",
          from: bounds.from,
          to: bounds.to,
        });
      }
      return;
    }
    if (reportType === "dateRange" && dateFrom && dateTo) {
      applyPreview({ rType: "dateRange", from: dateFrom, to: dateTo });
    }
  }, [reportType, sessionId, dateFrom, dateTo, selectedCategoryId, sessionStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  //Created By Cursor
  const handleReportTypeChange = (value) => {
    skipAutoPreviewRef.current = true;
    setReportType(value);
    if (value === "month") {
      setSelectedMonth("");
      setFileUrl("");
      setIframeLoading(false);
    }
    if (value === "today") {
      const today = formatDateLocal(new Date());
      setDateFrom(today);
      setDateTo(today);
      setDateRangePreset("");
    }
    if (value === "asOnDate") {
      const today = formatDateLocal(new Date());
      const start = parseLooseDate(sessionStartDate);
      setDateFrom(start ? formatDateLocal(start) : today);
      setDateTo(today);
      setDateRangePreset("");
    }
    if (value === "dateRange") {
      setDateRangePreset("");
    }
  };

  //Created By Cursor
  const handleCategorySelect = (categoryId) => {
    const nextId = String(categoryId || ALL_CATEGORY);
    setSelectedCategoryId(nextId);
    const cat =
      nextId === ALL_CATEGORY
        ? { category_id: ALL_CATEGORY, category_name: "All categories" }
        : categories.find((c) => String(c.category_id) === nextId) || {
            category_id: nextId,
            category_name: "Category",
          };
    if (reportType === "month" && !selectedMonth) return;
    applyPreview({ cat });
  };

  //Created By Cursor
  const handleMonthSelect = (ym) => {
    setSelectedMonth(ym);
    applyPreview({ rType: "month", monthYm: ym });
  };

  //Created By Cursor
  const HandleSelectedSession = (sid, sname, sstatus, startDate, endDate) => {
    bootstrappedRef.current = true;
    setSessionId(sid);
    setSessionName(sname);
    setSessionStatus(sstatus);
    setSessionStartDate(startDate || "");
    setSessionEndDate(endDate || "");
    setSelectedMonth("");
    setIsOpenSessionDialog(false);
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page tr-page trep-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Expense Report</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaChartBar />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Expense Report</h1>
              <p className="sm-hero__sub">
                Daybook entries · Session {sessionName || "—"}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <button
              type="button"
              onClick={() => loadPageData({ soft: true, sid: sessionId })}
              disabled={refreshing || loading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="tr-content trep-content">
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              setIsOpenSessionDialog(true);
            }}
            className="trep-session"
          >
            <span>
              Session: {sessionName || "—"}
              {sessionStatus ? ` (${sessionStatus})` : ""}
            </span>
            <img src={uploadIcon} alt="" className="trep-session__icon" />
          </Link>

          {loading ? (
            <div className="tr-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              {/* Report type: Today / Date Range / Month Wise */}
              <div style={{ padding: "4px 0 12px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {REPORT_TYPES.map((option) => {
                    const isSelected = reportType === option.value;
                    return (
                      <div
                        key={option.value}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleReportTypeChange(option.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleReportTypeChange(option.value);
                          }
                        }}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "10px",
                          border: isSelected
                            ? `2px solid ${option.color}`
                            : "2px solid #e5e7eb",
                          background: isSelected
                            ? `linear-gradient(135deg, ${option.color}15 0%, ${option.color}08 100%)`
                            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                          cursor: "pointer",
                          boxShadow: isSelected
                            ? `0 4px 12px ${option.color}30`
                            : "0 2px 4px rgba(0,0,0,0.05)",
                          transform: isSelected
                            ? "translateY(-2px)"
                            : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: isSelected
                                ? `linear-gradient(135deg, ${option.color} 0%, ${option.color}dd 100%)`
                                : `${option.color}20`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i
                              className={`fa ${option.icon}`}
                              style={{
                                fontSize: 16,
                                color: isSelected ? "#fff" : option.color,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              flex: 1,
                              fontSize: 14,
                              fontWeight: isSelected ? 700 : 600,
                              color: isSelected ? option.color : "#374151",
                            }}
                          >
                            {option.label}
                          </div>
                          {isSelected ? (
                            <i
                              className="fa fa-check-circle"
                              style={{ fontSize: 18, color: option.color }}
                            />
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Today */}
              {reportType === "today" ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
                    borderRadius: 12,
                    border: "2px solid #e5e7eb",
                    padding: "16px 20px",
                    marginBottom: 16,
                  }}
                >
                  <h5
                    style={{
                      margin: "0 0 12px",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <i
                      className="fa fa-calendar-day"
                      style={{ color: "#10b981" }}
                    />
                    Today
                  </h5>
                  <div
                    style={{
                      padding: 12,
                      background: "#ecfdf5",
                      borderRadius: 8,
                      border: "1px solid #86efac",
                    }}
                  >
                    <i
                      className="fa fa-info-circle me-2"
                      style={{ color: "#10b981" }}
                    />
                    <span
                      style={{
                        color: "#047857",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Report will be generated for today&apos;s expenses:{" "}
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* As On Date */}
              {reportType === "asOnDate" ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
                    borderRadius: 12,
                    border: "2px solid #e5e7eb",
                    padding: "16px 20px",
                    marginBottom: 16,
                  }}
                >
                  <h5
                    style={{
                      margin: "0 0 12px",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <i className="fa fa-history" style={{ color: "#06b6d4" }} />
                    As On Date
                  </h5>
                  <div
                    style={{
                      padding: 12,
                      background: "#ecfeff",
                      borderRadius: 8,
                      border: "1px solid #67e8f9",
                    }}
                  >
                    <i
                      className="fa fa-info-circle me-2"
                      style={{ color: "#06b6d4" }}
                    />
                    <span
                      style={{
                        color: "#0e7490",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      Report includes all expenses from session start
                      {sessionStartDate
                        ? ` (${parseLooseDate(sessionStartDate)?.toLocaleDateString?.() || sessionStartDate})`
                        : ""}{" "}
                      to today ({new Date().toLocaleDateString()}).
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Date Range */}
              {reportType === "dateRange" ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
                    borderRadius: 12,
                    border: "2px solid #e5e7eb",
                    padding: "12px 16px",
                    marginBottom: 16,
                  }}
                >
                  <h5
                    style={{
                      margin: "0 0 12px",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#111827",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <i
                      className="fa fa-calendar-alt"
                      style={{ color: "#3b82f6" }}
                    />
                    Date Range Selection
                  </h5>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(110px, 1fr))",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    {DATE_PRESETS.map((preset) => {
                      const isSelected = dateRangePreset === preset.value;
                      return (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => handleDateRangePreset(preset.value)}
                          style={{
                            padding: "10px 12px",
                            fontSize: 12,
                            fontWeight: isSelected ? 700 : 600,
                            borderRadius: 10,
                            border: isSelected
                              ? `2px solid ${preset.color}`
                              : "2px solid #e5e7eb",
                            background: isSelected
                              ? `linear-gradient(135deg, ${preset.color} 0%, ${preset.color}dd 100%)`
                              : "#fff",
                            color: isSelected ? "#fff" : "#374151",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <i className={`fa ${preset.icon}`} />
                          <span style={{ flex: 1, textAlign: "left" }}>
                            {preset.label}
                          </span>
                          {isSelected ? (
                            <i className="fa fa-check-circle" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "2px solid #e5e7eb",
                      padding: "8px 10px",
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                        background:
                          "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                        padding: "8px 12px",
                        borderRadius: 10,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => navigateCalendarMonth(-1)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: "1px solid #93c5fd",
                          background: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <i className="fa fa-chevron-left" />
                      </button>
                      <h6
                        style={{
                          margin: 0,
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#1e40af",
                        }}
                      >
                        {calendarMonth.toLocaleString("default", {
                          month: "short",
                          year: "numeric",
                        })}
                      </h6>
                      <button
                        type="button"
                        onClick={() => navigateCalendarMonth(1)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: "1px solid #93c5fd",
                          background: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <i className="fa fa-chevron-right" />
                      </button>
                    </div>

                    <div style={{ display: "flex", gap: 1, width: "100%" }}>
                      {generateCalendarDays.map((date) => {
                        const dateStr = formatDateLocal(date);
                        const inRange = isDateInRange(date);
                        const selected = isDateSelected(date);
                        const todayDate = isToday(date);
                        const isStart = dateStr === dateFrom;
                        const isEnd = dateStr === dateTo;
                        return (
                          <button
                            key={dateStr}
                            type="button"
                            onClick={() => handleCalendarDateClick(date)}
                            title={date.toLocaleDateString()}
                            style={{
                              padding: "4px 1px",
                              fontSize: 12,
                              fontWeight: selected ? 700 : 500,
                              borderRadius: "50%",
                              border: selected
                                ? "2px solid #3b82f6"
                                : inRange
                                ? "1px solid #bfdbfe"
                                : "1px solid transparent",
                              background:
                                isStart || isEnd
                                  ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                                  : inRange
                                  ? "#eff6ff"
                                  : todayDate
                                  ? "#fef3c7"
                                  : "#e2e8f0",
                              color:
                                isStart || isEnd
                                  ? "#fff"
                                  : todayDate && !inRange
                                  ? "#92400e"
                                  : "#374151",
                              cursor: "pointer",
                              flex: 1,
                              minWidth: 0,
                              minHeight: 32,
                              aspectRatio: "1",
                            }}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flex: 1,
                        minWidth: 200,
                      }}
                    >
                      <label
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#374151",
                          margin: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateFrom}
                        onChange={(e) => {
                          setDateFrom(e.target.value);
                          setDateRangePreset("custom");
                        }}
                        style={{
                          padding: "10px 14px",
                          fontSize: 14,
                          fontWeight: 600,
                          borderRadius: 10,
                          border: "2px solid #e5e7eb",
                          flex: 1,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flex: 1,
                        minWidth: 200,
                      }}
                    >
                      <label
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#374151",
                          margin: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        End Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={dateTo}
                        min={dateFrom}
                        onChange={(e) => {
                          setDateTo(e.target.value);
                          setDateRangePreset("custom");
                        }}
                        style={{
                          padding: "10px 14px",
                          fontSize: 14,
                          fontWeight: 600,
                          borderRadius: 10,
                          border: "2px solid #e5e7eb",
                          flex: 1,
                        }}
                      />
                    </div>
                  </div>
                  {dateFrom && dateTo ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 10,
                        background: "#eff6ff",
                        borderRadius: 8,
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <i
                        className="fa fa-info-circle me-2"
                        style={{ color: "#3b82f6" }}
                      />
                      <span
                        style={{
                          color: "#1e40af",
                          fontWeight: 500,
                          fontSize: 13,
                        }}
                      >
                        Selected: {new Date(dateFrom).toLocaleDateString()} -{" "}
                        {new Date(dateTo).toLocaleDateString()}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* Month Wise */}
              {reportType === "month" ? (
                <div
                  style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #fff 100%)",
                    borderRadius: 12,
                    border: "2px solid #e5e7eb",
                    padding: "16px 20px",
                    marginBottom: 16,
                    overflow: "hidden",
                  }}
                >
                  {monthOptions.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 10,
                        overflowX: "auto",
                        padding: 4,
                      }}
                    >
                      {monthOptions.map((option) => {
                        const isSelected = selectedMonth === option;
                        const date = new Date(`${option}-01T00:00:00`);
                        const monthName = date.toLocaleString("default", {
                          month: "long",
                        });
                        return (
                          <label
                            key={option}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 12px",
                              borderRadius: 10,
                              background: isSelected
                                ? "linear-gradient(135deg, #8b5cf620 0%, #8b5cf610 100%)"
                                : "#fff",
                              border: isSelected
                                ? "2px solid #8b5cf6"
                                : "2px solid #e5e7eb",
                              cursor: "pointer",
                              fontSize: 13,
                              fontWeight: isSelected ? 700 : 600,
                              color: isSelected ? "#8b5cf6" : "#64748b",
                              minWidth: 80,
                              flexShrink: 0,
                            }}
                          >
                            <input
                              type="radio"
                              name="expenseMonthSelection"
                              checked={isSelected}
                              onChange={() => handleMonthSelect(option)}
                              style={{
                                width: 16,
                                height: 16,
                                cursor: "pointer",
                                accentColor: "#8b5cf6",
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                lineHeight: 1.3,
                              }}
                            >
                              <span>{monthName}</span>
                              <span style={{ fontSize: 11, opacity: 0.8 }}>
                                ({option})
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 12,
                        background: "#fef3c7",
                        borderRadius: 8,
                        border: "1px solid #fbbf24",
                      }}
                    >
                      <i
                        className="fa fa-info-circle me-2"
                        style={{ color: "#f59e0b" }}
                      />
                      <span
                        style={{
                          color: "#92400e",
                          fontWeight: 500,
                          fontSize: 13,
                        }}
                      >
                        No months available for the selected session. Please
                        check session dates.
                      </span>
                    </div>
                  )}
                </div>
              ) : null}

              <section className="trep-panel" aria-label="Categories">
                <div className="trep-panel__head">
                  <div className="min-w-0">
                    <h2 className="trep-panel__title">
                      <FaFileAlt aria-hidden /> Category
                    </h2>
                    <p className="trep-panel__sub">
                      Showing: {selectedCategory.category_name}
                    </p>
                  </div>
                </div>
                <div className="trep-chips" role="group" aria-label="Categories">
                  <button
                    type="button"
                    className={`trep-chip${
                      selectedCategoryId === ALL_CATEGORY ? " is-active" : ""
                    }`}
                    onClick={() => handleCategorySelect(ALL_CATEGORY)}
                  >
                    {selectedCategoryId === ALL_CATEGORY ? (
                      <FaCheck aria-hidden />
                    ) : (
                      <FaFileAlt aria-hidden />
                    )}
                    <span>All categories</span>
                  </button>
                  {categories.map((cat) => {
                    const active =
                      String(selectedCategoryId) === String(cat.category_id);
                    return (
                      <button
                        key={cat.category_id}
                        type="button"
                        className={`trep-chip${active ? " is-active" : ""}`}
                        onClick={() => handleCategorySelect(cat.category_id)}
                      >
                        {active ? (
                          <FaCheck aria-hidden />
                        ) : (
                          <FaFileAlt aria-hidden />
                        )}
                        <span>{cat.category_name || "Unnamed"}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section
                className="trep-preview"
                aria-label="Expense report preview"
              >
                <div className="trep-preview__head">
                  <h2 className="trep-preview__title">Report preview</h2>
                  <p className="trep-preview__sub">
                    {reportType === "today"
                      ? "Today's expenses"
                      : reportType === "asOnDate"
                      ? "Session start to today"
                      : reportType === "dateRange"
                      ? "Selected date range"
                      : selectedMonth
                      ? `Month ${selectedMonth}`
                      : "Select a month to load"}{" "}
                    · {selectedCategory.category_name}
                  </p>
                </div>

                <div className="trep-preview__frame-wrap">
                  {iframeLoading ? (
                    <div className="trep-preview__loader">
                      <MyLoader />
                    </div>
                  ) : null}
                  {fileUrl ? (
                    <Iframe
                      key={fileUrl}
                      ref={iframeRef}
                      src={fileUrl}
                      width="100%"
                      height={height}
                      className="trep-preview__iframe"
                      id="expenseReportFrame"
                      scrolling="no"
                      onLoad={() => setIframeLoading(false)}
                    />
                  ) : (
                    <div className="trep-preview__empty">
                      {reportType === "month"
                        ? "Select a month to load the expense report."
                        : reportType === "dateRange"
                        ? "Select start and end dates to load the report."
                        : reportType === "asOnDate"
                        ? "Loading session-to-date expense report…"
                        : "Select filters to load the expense report."}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={HandleSelectedSession}
        />
      ) : null}
    </div>
  );
};

export default ExpenseReport;
